import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ThankYouPage = () => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  
  useEffect(() => {
    // Fade in animation for content
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/70 to-white flex flex-col items-center justify-center">
      <div className={`bg-white rounded-2xl shadow-md border border-gray-100 p-8 md:p-10 max-w-3xl w-full mx-auto transition-all duration-1000 ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
        <div className="max-w-2xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src="/new-logo-blue.png" alt="Careers Compass" className="h-10 object-contain" />
          </div>
          
          {/* Title and success icon */}
          <div className="flex flex-col items-center justify-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-lg mb-4">
              <Check size={40} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-center text-blue-600">提交成功</h2>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto my-4 rounded-full"></div>
          </div>
          
          {/* Updated Thank you message */}
          <div className="space-y-4 mb-8">
            <p className="text-gray-700 text-lg leading-relaxed">
              感谢你完成刚才的职业倾向测试。
            </p>
            <p className="text-gray-700 leading-relaxed">
              为了帮助我们为你提供更准确、更个性化的职业方向建议，接下来，我们的专属导师将根据你的问卷回答中可能存在的不一致或需要进一步澄清的部分，与你进行一通 10–15 分钟的快速电话沟通，以更准确地理解你的情况。
            </p>
            <p className="text-gray-700 leading-relaxed">
              在通话中，我们可能会请你分享一两个与你回答相关的小情境或真实经历，例如你在做决定或面对压力时的反应。这将帮助我们更全面地了解你，无需提前准备，如实表达即可。
            </p>
          </div>
          
          {/* WeChat QR Code section */}
          <div className="mb-8">
            {/* Header with icon */}
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-green-400 to-green-500 p-3 rounded-full shadow-md mr-3">
                <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.045c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-gray-800">
                添加小助手微信
              </h3>
            </div>

            <p className="text-gray-700 text-center leading-relaxed mb-6 text-lg">
              请添加下方小助手微信，预约一个方便的时间
            </p>

            {/* QR Code Container */}
            <div className="flex justify-center mb-4">
              <div className="bg-white p-4 rounded-xl shadow-xl">
                <img
                  src="/qrcode-new.jpg"
                  alt="小助手微信二维码"
                  className="w-56 h-56 object-contain"
                />
              </div>
            </div>

            {/* Helper text */}
            <div className="text-center">
              <p className="text-sm text-gray-500 flex items-center justify-center">
                <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                使用微信扫一扫识别二维码
              </p>
            </div>
          </div>
          
          {/* Return Home Button */}
          <button 
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center px-6 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all duration-300 text-gray-700 font-medium"
          >
            返回首页
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;